import * as React from "react";
import { ThemeProvider } from "styled-components";
import Body from "@/components/core-blocks/Body";
import CookieConsent from "@/components/core-blocks/CookieConsent";
import Footer from "@/components/core-blocks/Footer";
import Header from "@/components/core-blocks/Header";
import Main from "@/components/core-blocks/Main";
import Seo, { SEOProps } from "@/components/core-blocks/Seo";
import GlobalStyle, { MainInner, MainSection } from "@/styles/global";
import defaultTheme from "@/styles/themes/default.theme";
import S from "./event.styled";
import HeaderSubpage from "@/components/layout-blocks/HeaderSubpage";
import { IWPEvent } from "@/src/types";
import CalenderIcon from "@/assets/calendar.svg";
import ClockIcon from "@/assets/clock.svg";
import MapPinIcon from "@/assets/map-pin.svg";
import HandsIcon from "@/assets/hands.svg";
import Tag from "@/components/global-components/Tag";
import { getBgColor, translateColor } from "@/src/helpers";
import NewWindowIcon from "@/assets/new-window.svg";
import PrintIcon from "@/assets/print.svg";
import FacebookIcon from "@/assets/facebook.svg";
import { FacebookShareButton } from "react-share";
import { BreadCrumbProps } from "@/components/global-components/Breadcrumbs";

export type IEventTemplate = {
  pageContext: {
    title: string;
    uri: string;
    date: string;
    modified: string;
    seo: SEOProps;
    eventsInfo: IWPEvent;
    breadcrumb: BreadCrumbProps;
  };
};

const EventTemplate: React.FC<IEventTemplate> = ({
  pageContext: {
    title,
    date,
    uri,
    breadcrumb,
    eventsInfo: {
      tag,
      endDate,
      startTime,
      contentTitle,
      wysiwygContent,
      infoBox,
      image,
      linkBlock,
      datepicker,
      datepickerEnd,
      location,
      infoBoxButton,
      videoUrl,
      ingress,
    },
    seo,
  },
}) => {
  const tagArray = tag.split("|");
  const tagColor = tagArray[tagArray.length - 1];
  const infoBoxColor = getBgColor("light", translateColor(tagColor));
  const pageUrl = `https://stockholmsdf.se/${uri}`;

  const handlePrint = () => {
    window.print();
  };

  React.useEffect(() => {
    const followDiv = document.getElementById("followDiv");
    const contentDiv = document.querySelector("header");

    window.addEventListener("scroll", function () {
      if (window.scrollY > contentDiv!.offsetTop) {
        followDiv!.style.top =
          window.scrollY - contentDiv!.offsetTop + 150 + "px";
      } else {
        followDiv!.style.top = "150px";
      }
    });
  }, []);

  const renderLinkContainer = () => {
    const renderLinks = linkBlock?.map(
      ({ linkBlockTitle, linkBlockLink }, index) => {
        return (
          <S.LinkWrapper key={index}>
            {linkBlockTitle && <S.InfoTitle>{linkBlockTitle}</S.InfoTitle>}
            {linkBlockLink && <S.LinkTitle>{linkBlockLink.title}</S.LinkTitle>}
            {linkBlockLink && (
              <S.ExternLink href={linkBlockLink.url} target="_blank">
                {linkBlockLink.url}
                <NewWindowIcon />
              </S.ExternLink>
            )}
          </S.LinkWrapper>
        );
      }
    );

    return (
      <>
        {renderLinks}
        <S.LinkWrapper>
          <S.DetailsLarge>Publicerad: {date}</S.DetailsLarge>
        </S.LinkWrapper>
        <S.FlexWrapper>
          <S.InfoTitle>Dela</S.InfoTitle>
          <S.SoMeWrapper>
            <FacebookShareButton url={pageUrl}>
              <FacebookIcon />
            </FacebookShareButton>
          </S.SoMeWrapper>
        </S.FlexWrapper>

        <S.PrintButton onClick={handlePrint}>
          Skriv ut <PrintIcon />
        </S.PrintButton>
      </>
    );
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <GlobalStyle />
      <Body>
        <Header />
        <Main>
          <HeaderSubpage
            title={title}
            backgroundColor={translateColor(tagColor)}
            hasBackgroundImage={true}
            image={image}
            breadcrumb={breadcrumb}
          />
          <S.PrintTitle>{contentTitle}</S.PrintTitle>
          <MainSection>
            <S.Inner>
              <S.Wrapper>
                {videoUrl && (
                  <S.VideoWrapper>
                    <S.VimeoHero
                      dangerouslySetInnerHTML={{ __html: videoUrl }}
                    />
                    <S.IconWrapper>
                      <HandsIcon />
                    </S.IconWrapper>
                  </S.VideoWrapper>
                )}
                {tag && <Tag tag={tag} />}
                <S.TextContainer>
                  {contentTitle && <S.H2>{contentTitle}</S.H2>}
                  {ingress && <S.Ingress>{ingress}</S.Ingress>}
                  {wysiwygContent && (
                    <S.TextContent
                      dangerouslySetInnerHTML={{ __html: wysiwygContent }}
                    />
                  )}
                </S.TextContainer>
              </S.Wrapper>
              <S.InfoContainer $bgColor={infoBoxColor} id="followDiv">
                {datepicker && (
                  <S.InfoWrapper>
                    <S.InfoTitle>
                      <CalenderIcon />
                      Datum
                    </S.InfoTitle>
                    <S.InfoText>
                      {datepicker}
                      {datepickerEnd && " - " + datepickerEnd}
                    </S.InfoText>
                  </S.InfoWrapper>
                )}
                {startTime && (
                  <S.InfoWrapper>
                    <S.InfoTitle>
                      <ClockIcon />
                      Tid
                    </S.InfoTitle>
                    <S.InfoText>
                      {startTime}
                      {endDate && " - " + endDate}
                    </S.InfoText>
                  </S.InfoWrapper>
                )}
                {location && (
                  <S.InfoWrapper>
                    <S.InfoTitle>
                      <MapPinIcon />
                      Plats
                    </S.InfoTitle>
                    <S.InfoText>{location}</S.InfoText>
                  </S.InfoWrapper>
                )}
                {infoBox?.map(({ infoTitle, infoText }, index) => {
                  return (
                    <S.InfoWrapper key={index}>
                      <S.InfoTitle>{infoTitle}</S.InfoTitle>
                      <S.InfoText>{infoText}</S.InfoText>
                    </S.InfoWrapper>
                  );
                })}
                {infoBoxButton && (
                  <S.ExternButton href={infoBoxButton.url} target="_blank">
                    {infoBoxButton.title}
                  </S.ExternButton>
                )}
              </S.InfoContainer>
            </S.Inner>
          </MainSection>
          <MainSection $bgColor={infoBoxColor}>
            <MainInner>
              <S.LinkBlockContainer>
                {renderLinkContainer()}
              </S.LinkBlockContainer>
            </MainInner>
          </MainSection>
          <Footer />
          <CookieConsent />
        </Main>
      </Body>
    </ThemeProvider>
  );
};

export default EventTemplate;

// eslint-disable-next-line require-jsdoc
export function Head({ pageContext }: IEventTemplate) {
  return (
    <>
      <html lang="sv" />
      <Seo {...pageContext.seo} lang="sv" />
    </>
  );
}
